import { useState, useCallback, useRef, useEffect } from 'react';
import { climbsService } from '../services/climbs';

interface UseTitleValidationOptions {
  locationId: string | number | null;
  /**
   * The scope the name has to be unique in, beyond the location: its type, or —
   * for a board problem — the board it goes on and the angle it is set at. The
   * server checks the same way, so leaving these out asks a wider question than
   * the save will (see climbsService.validateTitle).
   */
  type?: 'boulder' | 'route' | 'board_climb' | null;
  boardId?: string | number | null;
  boardTypeId?: string | number | null;
  inclination?: number | null;
  excludeClimbId?: string | null;
  debounceMs?: number;
}

interface TitleValidationResult {
  validating: boolean;
  exists: boolean;
  errorMessage: string | null;
  validate: (title: string) => void;
  reset: () => void;
}

/**
 * Hook for debounced title validation with abort controller
 *
 * @param options - Configuration options
 * @returns Validation state and control methods
 */
export function useTitleValidation({
  locationId,
  type,
  boardId,
  boardTypeId,
  inclination,
  excludeClimbId,
  debounceMs = 500
}: UseTitleValidationOptions): TitleValidationResult {
  const [validating, setValidating] = useState(false);
  const [exists, setExists] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastValidatedRef = useRef<string>('');

  // What the last answer was about. A name is only "already asked" while the
  // scope it was asked in is still the same one — changing the angle of a board
  // problem asks a different question of the same name.
  const scopeKey = [locationId, type, boardId, boardTypeId, inclination, excludeClimbId].join('|');

  const validate = useCallback((title: string) => {
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Abort previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }

    // Reset state
    setExists(false);
    setErrorMessage(null);

    // Skip validation if there is no title, or nowhere to check it against — a
    // board problem answers with its board rather than with a location.
    if (!title.trim() || !(locationId || boardId || boardTypeId)) {
      setValidating(false);
      lastValidatedRef.current = '';
      return;
    }

    // Skip if the same name was already checked in the same scope
    if (`${title.trim()}|${scopeKey}` === lastValidatedRef.current) {
      return;
    }

    // Debounce validation
    debounceRef.current = setTimeout(async () => {
      setValidating(true);
      abortRef.current = new AbortController();
      lastValidatedRef.current = `${title.trim()}|${scopeKey}`;

      try {
        const result = await climbsService.validateTitle({
          title,
          locationId,
          type,
          boardId,
          boardTypeId,
          inclination,
          excludeClimbId,
        });

        // Check if request was aborted
        if (abortRef.current?.signal.aborted) {
          return;
        }

        if (result.exists) {
          setExists(true);
          setErrorMessage(result.message || 'Un climb con questo titolo esiste già');
        }
      } catch (error) {
        // Silent fail - don't block the user
        console.error('Title validation failed:', error);
      } finally {
        setValidating(false);
      }
    }, debounceMs);
  }, [locationId, type, boardId, boardTypeId, inclination, excludeClimbId, scopeKey, debounceMs]);

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    setValidating(false);
    setExists(false);
    setErrorMessage(null);
    lastValidatedRef.current = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return { validating, exists, errorMessage, validate, reset };
}
